import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
// 👇 Usa tu axiosInstance (así mantienes baseURL y futuros cambios en un solo lugar)
import axiosInstance from '../api/axiosInstance';
import {
  Container, Box, Typography, TextField, Button, CircularProgress,
  Alert, Fade, Slide
} from '@mui/material';
import { motion } from 'framer-motion';
import logoImage from './logo.png';

const LoginPage = ({ onLoginSuccess }) => {
  const [credentials, setCredentials] = useState({ correo: '', contraseña: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // Enviamos ambas llaves por compatibilidad: "contraseña" y "password"
      const payload = {
        correo: credentials.correo,
        contraseña: credentials.contraseña,
        password: credentials.contraseña,
      };

      // Tu backend ahora está bajo /api/login
      const res = await axiosInstance.post('/login', payload, {
        headers: { 'Content-Type': 'application/json' }
      });

      // Aceptamos distintos nombres de campo por si el backend devuelve otro
      let tok = res?.data?.access_token || res?.data?.token || res?.data?.jwt;
      if (!tok || typeof tok !== 'string' || tok.trim() === '') {
        setError('El servidor no devolvió un token válido.');
        return;
      }

      // Normaliza: si vino con "Bearer ..." guardamos solo el JWT
      if (tok.startsWith('Bearer ')) tok = tok.slice(7);

      localStorage.setItem('token', tok);
      localStorage.setItem('nombre', res?.data?.nombre || credentials.correo);
      if (res?.data?.perfil) localStorage.setItem('perfil', res.data.perfil);

      onLoginSuccess?.();
      navigate('/');

    } catch (err) {
      // Limpia token basura si hubo error
      localStorage.removeItem('token');
      setError(err?.response?.data?.detail || 'Error al iniciar sesión');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
               background: 'linear-gradient(to bottom, #ffffff 0%, #e6f7ff 100%)', p: 2 }}>
      <Container maxWidth="xs">
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', p: 4, borderRadius: 3,
                   boxShadow: '0 4px 20px rgba(0, 150, 200, 0.1)', bgcolor: 'rgba(255,255,255,0.95)',
                   border: '1px solid rgba(200, 230, 255, 0.5)' }}>
          <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 1, color: '#0077b6', textAlign: 'center',
                                          fontSize: '2.2rem', letterSpacing: '1px',
                                          textShadow: '0 2px 4px rgba(0,119,182,0.1)' }}>
            BATSA
          </Typography>

          <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ duration: 0.5 }}
                      style={{ marginBottom: 24, width: 120, height: 120 }}>
            <img src={logoImage} alt="Logo Sistema"
                 style={{ width: '100%', height: '100%', objectFit: 'contain',
                          filter: 'drop-shadow(0 2px 4px rgba(0,119,182,0.2))' }} />
          </motion.div>

          <Slide in direction="down" timeout={500}>
            <Typography component="h2" variant="h5" sx={{
              fontWeight: 'bold', mb: 3, color: '#0096c7', fontSize: '1.6rem', position: 'relative',
              '&::after': { content: '""', display: 'block', width: 50, height: 3, background: '#90e0ef',
                            m: '8px auto 0', borderRadius: 3 }
            }}>
              Iniciar Sesión
            </Typography>
          </Slide>

          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1, width: '100%' }}>
            <Fade in={!!error} timeout={500}>
              <Box sx={{ mb: 2 }}>
                {error && <Alert severity="error" sx={{ borderRadius: 1 }}>{error}</Alert>}
              </Box>
            </Fade>

            <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.2 }}>
              <TextField
                margin="normal" required fullWidth label="Correo electrónico" name="correo" autoComplete="email" autoFocus
                value={credentials.correo}
                onChange={(e) => setCredentials({ ...credentials, correo: e.target.value })}
                sx={{ '& .MuiOutlinedInput-root': { '& fieldset': { borderColor: '#90e0ef' },
                      '&:hover fieldset': { borderColor: '#00b4d8' } } }}
              />
            </motion.div>

            <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.3 }}>
              <TextField
                margin="normal" required fullWidth label="Contraseña" type="password" name="contraseña"
                autoComplete="current-password"
                value={credentials.contraseña}
                onChange={(e) => setCredentials({ ...credentials, contraseña: e.target.value })}
                sx={{ '& .MuiOutlinedInput-root': { '& fieldset': { borderColor: '#90e0ef' },
                      '&:hover fieldset': { borderColor: '#00b4d8' } } }}
              />
            </motion.div>

            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.4 }} whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
              <Button type="submit" fullWidth variant="contained"
                      sx={{ mt: 3, mb: 2, height: 48, fontSize: '1rem', fontWeight: 'bold',
                            background: 'linear-gradient(to right, #0096c7, #00b4d8)',
                            boxShadow: '0 2px 10px rgba(0, 180, 216, 0.3)',
                            '&:hover': { background: 'linear-gradient(to right, #0077b6, #0096c7)' } }}
                      disabled={loading}>
                {loading ? <CircularProgress size={24} sx={{ color: 'white' }} /> : 'Iniciar Sesión'}
              </Button>
            </motion.div>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default LoginPage;
